import * as React from "react";
import { useTranslation } from "react-i18next";

// --- Icons ---
import { TextColorIcon } from "@/components/_organisms/tiptap/tiptap-icons/text-color-icon";
// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/_organisms/tiptap/tiptap-ui-primitive/popover";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { type Editor } from "@tiptap/react";

export interface TextColorButtonProps extends Omit<ButtonProps, "type"> {
  /**
   * The TipTap editor instance.
   */
  editor?: Editor;
  /**
   * Whether the button should be hidden when unavailable.
   * @default false
   */
  hideWhenUnavailable?: boolean;
}

export const TEXT_COLOR_PALETTE = [
  // Row 1: Grayscale/Neutral Tones
  "#1a1a1a",
  "#333333",
  "#4d4d4d",
  "#666666",
  "#808080",
  "#999999",
  "#b3b3b3",
  "#cccccc",
  "#e6e6e6",
  "#ffffff",
  // Row 2: Vibrant/Primary Colors
  "#ff0000",
  "#ff6600",
  "#ffcc00",
  "#66ff00",
  "#00ffff",
  "#0066ff",
  "#6600ff",
  "#cc00ff",
  "#ff00cc",
  "#ff0066",
  // Row 3: Light/Pastel Colors
  "#ffcccc",
  "#ffd9cc",
  "#ffe6cc",
  "#ccffcc",
  "#ccffff",
  "#cce6ff",
  "#e6ccff",
  "#ffccff",
  "#ffcce6",
  "#ffccd9",
  // Row 4: Medium Saturated Colors
  "#ff9999",
  "#ffb366",
  "#ffcc66",
  "#99ff99",
  "#99ffff",
  "#99ccff",
  "#cc99ff",
  "#ff99ff",
  "#ff99cc",
  "#ff99b3",
  // Row 5: Deep/Rich Colors
  "#cc6666",
  "#cc8c4d",
  "#cc994d",
  "#66cc66",
  "#66cccc",
  "#6699cc",
  "#9966cc",
  "#cc66cc",
  "#cc6699",
  "#cc6680",
  // Row 6: Dark/Jewel Tones
  "#993333",
  "#996633",
  "#997333",
  "#339933",
  "#339999",
  "#336699",
  "#663399",
  "#993399",
  "#993366",
  "#99334d",
];

export function canSetTextColor(editor: Editor | null): boolean {
  if (!editor) return false;
  // Check if the editor can set the textStyle mark with color
  try {
    return editor.can().setMark("textStyle", { color: "#000000" });
  } catch {
    return false;
  }
}

export function isTextColorActive(editor: Editor | null, color: string): boolean {
  if (!editor) return false;
  // Check if the textStyle mark is active with the specific color
  return editor.isActive("textStyle", { color });
}

export function getCurrentTextColor(editor: Editor | null): string | null {
  if (!editor) return null;
  // Get the current text color from the active textStyle mark
  const marks = editor.getAttributes("textStyle");
  return marks.color || null;
}

export function setTextColor(editor: Editor | null, color: string): void {
  if (!editor) return;
  // Set the text color using the textStyle mark
  editor.chain().focus().setMark("textStyle", { color }).run();
}

export function removeTextColor(editor: Editor | null): void {
  if (!editor) return;
  // Remove the text color by unsetting the textStyle mark
  editor.chain().focus().unsetMark("textStyle").run();
}

export const TextColorButton = React.forwardRef<HTMLButtonElement, TextColorButtonProps>(
  ({ editor: providedEditor, size = "base", hideWhenUnavailable = false, ...props }, ref) => {
    const { t } = useTranslation("common");
    const editor = useTiptapEditor(providedEditor);
    const [isOpen, setIsOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");

    const canToggle = React.useMemo(() => canSetTextColor(editor), [editor]);
    const currentColor = getCurrentTextColor(editor);
    const isActive = React.useMemo(() => isTextColorActive(editor, currentColor || ""), [editor, currentColor]);

    const handleColorChange = React.useCallback(
      (color: string) => {
        if (editor && canToggle) {
          setTextColor(editor, color);
          setInputValue(color);
        }
      },
      [editor, canToggle],
    );

    const handleInputChange = React.useCallback(
      (value: string) => {
        setInputValue(value);

        // Check if it's a valid hex color
        if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
          handleColorChange(value);
        }
      },
      [handleColorChange],
    );

    // Sync input value with current color when it changes
    React.useEffect(() => {
      if (currentColor) {
        setInputValue(currentColor);
      }
    }, [currentColor]);

    if (hideWhenUnavailable && !canToggle) {
      return null;
    }

    if (!editor || !editor.isEditable) {
      return null;
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            data-style="ghost"
            data-active-state={isActive ? "on" : "off"}
            role="button"
            aria-label={t("editor.textColor")}
            aria-pressed={isActive}
            tooltip={t("editor.textColor")}
            size={size}
            {...props}
            ref={ref}
          >
            <TextColorIcon className="tiptap-button-icon" />
            {currentColor && (
              <div className="h-3 w-3 rounded-full border border-border" style={{ backgroundColor: currentColor }} />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-4">
          <div className="space-y-4">
            <div className="grid grid-cols-10 gap-1">
              {TEXT_COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  className={`h-6! w-6! rounded-full border-2 transition-all hover:scale-110 ${
                    currentColor === color ? "border-foreground" : "border-border"
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(color)}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>

            {/* Custom Color Input */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground">{t("editor.customColor")}</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={currentColor || "#000000"}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="h-8 w-16 rounded-sm border border-border"
                />
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.target.value)}
                  placeholder={t("editor.hexPlaceholder")}
                  className="flex-1 rounded-sm border border-border px-2 py-1 text-xs"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);

TextColorButton.displayName = "TextColorButton";

export default TextColorButton;
